import { useState } from "react";
import {
	Alert,
	Badge,
	Box,
	Button,
	ButtonGroup,
	Checkbox,
	Chip,
	CircularProgress,
	FormControlLabel,
	IconButton,
	InputAdornment,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Paper,
	Popover,
	Select,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { ContentCopy, FilterList, Link, Search, Check } from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import type { Deal, Job } from "../../../models";
import {
	useUnmatchedDealsViewModel,
	UNASSIGNED_DROPPABLE_ID,
	makeJobDraggableId,
	parseJobDraggableId,
} from "./UnmatchedDeals.vm";
import type { DealSortOption } from "./UnmatchedDeals.vm";
import { PortalTooltip } from "../../helpers";
import { toast } from "react-toastify";

// region DealPropertyChip

function DealPropertyChip({
	label,
	value,
	onClick,
	isFilterActive,
	isHighlighted,
	onMouseEnter,
	onMouseLeave,
}: {
	label: string;
	value: string;
	onClick?: () => void;
	isFilterActive?: boolean;
	isHighlighted?: boolean;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
}) {
	return (
		<Chip
			label={
				label ? (
					<>
						<span style={{ fontWeight: 700 }}>{label}:</span> {value}
					</>
				) : (
					value
				)
			}
			size="small"
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			sx={{
				bgcolor: isHighlighted ? "#E3F2FD" : "transparent",
				color: "text.primary",
				fontWeight: 400,
				fontSize: "0.7rem",
				height: 24,
				cursor: "pointer",
				outline: isFilterActive ? "2px solid" : "none",
				outlineColor: "primary.main",
				transition: "background-color 0.15s",
			}}
		/>
	);
}

// endregion

// region JobCard

function JobCard({
	job,
	index,
	isSelected,
	onToggleSelect,
	multiDragCount = 0,
	vm,
}: {
	job: Job;
	index: number;
	isSelected?: boolean;
	onToggleSelect?: (draggableId: string, shiftKey: boolean) => void;
	multiDragCount?: number;
	vm: ReturnType<typeof useUnmatchedDealsViewModel>;
}) {
	const draggableId = makeJobDraggableId(job.id);
	const toggleFilter = (propertyName: string, value: string) => {
		const current = vm.dealFilters[propertyName] || new Set<string>();
		const next = new Set(current);
		if (next.has(value)) next.delete(value);
		else next.add(value);
		vm.setDealFilter(propertyName, next);
	};
	const chipProps = (propName: string, label: string, value: string) => ({
		label,
		value,
		onClick: () => toggleFilter(propName, value),
		isFilterActive: vm.dealFilters[propName]?.has(value) ?? false,
		isHighlighted: vm.hoveredChip?.propertyName === propName && vm.hoveredChip?.value === value,
		onMouseEnter: () => vm.setHoveredChip({ propertyName: propName, value }),
		onMouseLeave: () => vm.setHoveredChip(null),
	});

	const [idMenuAnchor, setIdMenuAnchor] = useState<null | HTMLElement>(null);

	return (
		<Draggable draggableId={draggableId} index={index}>
			{(provided, snapshot) => {
				const isDimmed = !snapshot.isDragging && multiDragCount > 0;
				const showBadge = snapshot.isDragging && multiDragCount > 1;

				return (
					<Paper
						ref={provided.innerRef}
						{...provided.draggableProps}
						{...provided.dragHandleProps}
						elevation={snapshot.isDragging ? 4 : 1}
						onClick={(e) => onToggleSelect?.(draggableId, e.shiftKey)}
						sx={{
							p: 1.5,
							mb: 0.75,
							position: "relative",
							bgcolor: snapshot.isDragging ? "action.hover" : isSelected ? "primary.50" : "background.paper",
							cursor: "grab",
							"&:active": { cursor: "grabbing" },
							userSelect: "none",
							opacity: isDimmed ? 0.4 : 1,
							transition: "opacity 0.15s",
						}}
					>
						{showBadge && (
							<Chip
								label={multiDragCount}
								color="primary"
								size="small"
								sx={{
									position: "absolute",
									top: -8,
									right: -8,
									height: 22,
									minWidth: 22,
									fontWeight: 700,
									fontSize: "0.75rem",
									zIndex: 1,
								}}
							/>
						)}
						<Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
							<Checkbox size="small" checked={!!isSelected} tabIndex={-1} disableRipple sx={{ p: 0, mr: 0.5 }} />
							<Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
								{job.fieldName || "Unnamed Field"}
							</Typography>
							<Chip
								label={job.id}
								size="small"
								clickable
								onClick={(e) => {
									e.stopPropagation();
									setIdMenuAnchor(e.currentTarget);
								}}
								sx={{ height: 18, fontSize: "0.6rem", ml: 0.5, color: "text.secondary" }}
							/>
							<Menu
								anchorEl={idMenuAnchor}
								open={Boolean(idMenuAnchor)}
								onClose={(e: React.SyntheticEvent) => {
									e.stopPropagation?.();
									setIdMenuAnchor(null);
								}}
								onClick={(e) => e.stopPropagation()}
								anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
								transformOrigin={{ vertical: "top", horizontal: "right" }}
							>
								<MenuItem
									onClick={(e) => {
										e.stopPropagation();
										navigator.clipboard.writeText(job.url);
										toast.success("Airtable URL copied");
										setIdMenuAnchor(null);
									}}
									sx={{ fontSize: "0.8rem" }}
								>
									<ListItemIcon>
										<Link fontSize="small" />
									</ListItemIcon>
									<ListItemText>Copy Airtable URL</ListItemText>
								</MenuItem>
								<MenuItem
									onClick={(e) => {
										e.stopPropagation();
										navigator.clipboard.writeText(job.id);
										toast.success("Record ID copied");
										setIdMenuAnchor(null);
									}}
									sx={{ fontSize: "0.8rem" }}
								>
									<ListItemIcon>
										<ContentCopy fontSize="small" />
									</ListItemIcon>
									<ListItemText>Copy ID</ListItemText>
								</MenuItem>
							</Menu>
						</Box>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5 }}>
							<Chip
								label={
									<>
										<span style={{ fontWeight: 700 }}>Grower:</span> {job.growerName || "—"}
									</>
								}
								size="small"
								sx={{ height: 22, fontSize: "0.7rem", bgcolor: "transparent", border: "none" }}
							/>
							{job.farmName && (
								<Chip
									label={
										<>
											<span style={{ fontWeight: 700 }}>Farm:</span> {job.farmName}
										</>
									}
									size="small"
									sx={{ height: 22, fontSize: "0.7rem", bgcolor: "transparent", border: "none" }}
								/>
							)}
							<Chip
								label={
									<>
										<span style={{ fontWeight: 700 }}>Company:</span> {job.companyName || "—"}
									</>
								}
								size="small"
								sx={{ height: 22, fontSize: "0.7rem", bgcolor: "transparent", border: "none" }}
							/>
							{job.status && (
								<Chip
									label={
										<>
											<span style={{ fontWeight: 700 }}>Status:</span> {job.status}
										</>
									}
									size="small"
									sx={{ height: 22, fontSize: "0.7rem", bgcolor: "transparent", border: "none" }}
								/>
							)}
							{job.submitterEmail && (
								<Chip
									label={
										<>
											<span style={{ fontWeight: 700 }}>Submitter:</span> {job.submitterEmail}
										</>
									}
									size="small"
									sx={{ height: 22, fontSize: "0.7rem", bgcolor: "transparent", border: "none" }}
								/>
							)}
							{job.importDensity != null && job.importDensity > 0 && (
								<DealPropertyChip {...chipProps("density", "Density", `${job.importDensity} acre`)} />
							)}
							{job.importDepth != null && job.importDepth > 0 && (
								<DealPropertyChip {...chipProps("depth", "Depth", `${job.importDepth} in`)} />
							)}
							{job.importLabName && <DealPropertyChip {...chipProps("labName", "Lab", job.importLabName)} />}
							{job.boundaryAcres > 0 && (
								<Chip
									label={
										<>
											<span style={{ fontWeight: 700 }}>Acres:</span> {job.boundaryAcres.toFixed(1)}
										</>
									}
									size="small"
									sx={{ height: 22, fontSize: "0.7rem", bgcolor: "transparent", border: "none" }}
								/>
							)}
							{job.season && <DealPropertyChip {...chipProps("season", "Season", job.season)} />}
							{job.externalTestPackageId && (
								<DealPropertyChip {...chipProps("testPackage", "Test Package", job.externalTestPackageId)} />
							)}
							{job.externalFrequencyOfMicros != null && job.externalFrequencyOfMicros > 0 && (
								<DealPropertyChip
									{...chipProps("frequencyOfMicros", "Freq of Micros", `${job.externalFrequencyOfMicros}`)}
								/>
							)}
						</Box>
						{job.errorNotes.filter((e) => e.source === "Deal Matcher").length > 0 && (
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
								{job.errorNotes
									.filter((e) => e.source === "Deal Matcher")
									.map((e, i) => {
										const multiMatch = e.note.match(/Multiple matching deals found\s*\(([^)]+)\)?/);
										if (multiMatch) {
											const dealIds = multiMatch[1].split(",").map((s) => s.trim());
											return (
												<span
													key={i}
													onClick={(ev) => {
														ev.stopPropagation();
														vm.loadDealsByIds(dealIds);
													}}
												>
													<PortalTooltip title={e.note}>
														<Chip
															label={`Matched multiple deals (${dealIds.length})`}
															size="small"
															color="error"
															variant="outlined"
															clickable
															sx={{ height: 22, fontSize: "0.7rem" }}
														/>
													</PortalTooltip>
												</span>
											);
										}
										return (
											<Chip
												key={i}
												label={e.note}
												size="small"
												color="error"
												variant="outlined"
												sx={{ height: 22, fontSize: "0.7rem" }}
											/>
										);
									})}
							</Box>
						)}
					</Paper>
				);
			}}
		</Draggable>
	);
}

// endregion

// region DealsHeader

function DealsHeader({ vm }: { vm: ReturnType<typeof useUnmatchedDealsViewModel> }) {
	const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
	const isFiltering = vm.activeDealFilterCount > 0 || vm.dealSearchText.trim().length > 0;
	const totalCount = vm.deals.length;
	const shownCount = vm.filteredSortedDeals.length;

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
			<Typography variant="subtitle2" sx={{ whiteSpace: "nowrap" }}>
				Deals {isFiltering ? `(${shownCount} / ${totalCount})` : `(${totalCount})`}
			</Typography>
			<TextField
				size="small"
				placeholder="Search deals..."
				value={vm.dealSearchText}
				onChange={(e) => vm.setDealSearchText(e.target.value)}
				slotProps={{
					input: {
						startAdornment: (
							<InputAdornment position="start">
								<Search fontSize="small" />
							</InputAdornment>
						),
						sx: { fontSize: "0.8rem", height: 28 },
					},
				}}
				sx={{ ml: "auto", width: 180 }}
			/>
			<Select
				size="small"
				value={vm.dealSortOption}
				onChange={(e: SelectChangeEvent) => vm.setDealSortOption(e.target.value as DealSortOption)}
				sx={{
					fontSize: "0.75rem",
					height: 28,
					minWidth: 0,
					"& .MuiSelect-select": { py: 0.25, pr: "24px !important", pl: 1 },
				}}
			>
				<MenuItem value="default" sx={{ fontSize: "0.8rem" }}>
					Default
				</MenuItem>
				<MenuItem value="density-asc" sx={{ fontSize: "0.8rem" }}>
					Density ↑
				</MenuItem>
				<MenuItem value="density-desc" sx={{ fontSize: "0.8rem" }}>
					Density ↓
				</MenuItem>
				<MenuItem value="depth-asc" sx={{ fontSize: "0.8rem" }}>
					Depth ↑
				</MenuItem>
				<MenuItem value="depth-desc" sx={{ fontSize: "0.8rem" }}>
					Depth ↓
				</MenuItem>
			</Select>
			<PortalTooltip title="Filter deals">
				<IconButton size="small" onClick={(e) => setFilterAnchorEl(e.currentTarget)}>
					<Badge badgeContent={vm.activeDealFilterCount} color="primary" overlap="circular">
						<FilterList fontSize="small" />
					</Badge>
				</IconButton>
			</PortalTooltip>
			<Popover
				open={Boolean(filterAnchorEl)}
				anchorEl={filterAnchorEl}
				onClose={() => setFilterAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<Box sx={{ p: 1, maxHeight: 320, overflowY: "auto" }}>
					{vm.dealProperties.map((prop) => {
						const options = vm.dealFilterOptions[prop.name] || [];
						if (options.length <= 1) return null;
						const activeSet = vm.dealFilters[prop.name] || new Set<string>();
						return (
							<Box key={prop.name} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, flexWrap: "wrap" }}>
								<Typography
									variant="caption"
									color="text.secondary"
									sx={{ fontWeight: 600, fontSize: "0.65rem", minWidth: 48 }}
								>
									{prop.label}:
								</Typography>
								{options.map((value) => {
									const isActive = activeSet.has(value);
									return (
										<Chip
											key={value}
											label={value}
											size="small"
											variant={isActive ? "filled" : "outlined"}
											color={isActive ? "primary" : "default"}
											onClick={() => {
												const next = new Set(activeSet);
												if (isActive) next.delete(value);
												else next.add(value);
												vm.setDealFilter(prop.name, next);
											}}
											sx={{ fontSize: "0.65rem", height: 22, cursor: "pointer" }}
										/>
									);
								})}
							</Box>
						);
					})}
					{isFiltering && (
						<Button
							size="small"
							onClick={() => {
								vm.clearDealFilters();
								setFilterAnchorEl(null);
							}}
							sx={{ mt: 1, width: "100%" }}
						>
							Clear All
						</Button>
					)}
				</Box>
			</Popover>
		</Box>
	);
}

// endregion

// region DealCard

function DealCard({ deal, vm }: { deal: Deal; vm: ReturnType<typeof useUnmatchedDealsViewModel> }) {
	const stagedJobs = vm.jobsByDeal.get(deal.id) || [];
	const toggleFilter = (propertyName: string, value: string) => {
		const current = vm.dealFilters[propertyName] || new Set<string>();
		const next = new Set(current);
		if (next.has(value)) next.delete(value);
		else next.add(value);
		vm.setDealFilter(propertyName, next);
	};
	const isActive = (propertyName: string, value: string) => vm.dealFilters[propertyName]?.has(value) ?? false;
	const isHovered = (propertyName: string, value: string) =>
		vm.hoveredChip?.propertyName === propertyName && vm.hoveredChip?.value === value;

	const chipProps = (propName: string, label: string, value: string) => ({
		label,
		value,
		onClick: () => toggleFilter(propName, value),
		isFilterActive: isActive(propName, value),
		isHighlighted: isHovered(propName, value),
		onMouseEnter: () => vm.setHoveredChip({ propertyName: propName, value }),
		onMouseLeave: () => vm.setHoveredChip(null),
	});

	return (
		<Droppable droppableId={deal.id}>
			{(provided, snapshot) => (
				<Paper
					ref={provided.innerRef}
					{...provided.droppableProps}
					variant="outlined"
					sx={{
						p: 1.5,
						bgcolor: snapshot.isDraggingOver ? "primary.50" : "background.paper",
						borderColor: snapshot.isDraggingOver ? "primary.main" : "divider",
						borderWidth: snapshot.isDraggingOver ? 2 : 1,
						transition: "border-color 0.15s, background-color 0.15s",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{deal.name}
						</Typography>
					</Box>
					<Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
						{!deal.isActive && (
							<Chip label="Inactive" size="small" color="error" sx={{ height: 18, fontSize: "0.6rem" }} />
						)}
						{vm.companyNameMap.get(deal.companyId) && (
							<DealPropertyChip {...chipProps("company", "Company", vm.companyNameMap.get(deal.companyId)!)} />
						)}
						<DealPropertyChip {...chipProps("density", "Density", `${deal.density} acre`)} />
						<DealPropertyChip {...chipProps("depth", "Depth", `${deal.depth} in`)} />
						<DealPropertyChip {...chipProps("type", "Type", deal.type)} />
						{(deal.isComposite || deal.isMultiComposite) && (
							<DealPropertyChip
								{...chipProps("composite", "", deal.isMultiComposite ? "Multi-Composite" : "Composite")}
							/>
						)}
						<DealPropertyChip {...chipProps("testPackage", "Package", deal.testPackage || "None")} />
						<DealPropertyChip {...chipProps("season", "Season", deal.season)} />
						<DealPropertyChip {...chipProps("frequencyOfMicros", "Micros", `${deal.frequencyOfMicros ?? 0}`)} />
						{deal.labName && <DealPropertyChip {...chipProps("labName", "Lab", deal.labName)} />}
						{deal.labAccountNumber && (
							<DealPropertyChip {...chipProps("labAccountNumber", "Lab Acct", deal.labAccountNumber)} />
						)}
						{deal.syncMethod && <DealPropertyChip {...chipProps("syncMethod", "Sync", deal.syncMethod)} />}
						{deal.specialTag && <DealPropertyChip {...chipProps("specialTag", "Tag", deal.specialTag)} />}
						{deal.isPtsRequired && <DealPropertyChip {...chipProps("isPtsRequired", "", "Pts Required")} />}
						{deal.customizer && <DealPropertyChip {...chipProps("customizer", "Customizer", deal.customizer)} />}
					</Box>

					<Box sx={{ minHeight: 36 }}>
						{stagedJobs.length === 0 && !snapshot.isDraggingOver && (
							<Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>
								Drop jobs here
							</Typography>
						)}
						{stagedJobs.map((job, idx) => (
							<JobCard
								key={makeJobDraggableId(job.id)}
								job={job}
								index={idx}
								isSelected={vm.selectedJobs.has(makeJobDraggableId(job.id))}
								onToggleSelect={vm.handleItemClick}
								multiDragCount={vm.selectedJobs.has(makeJobDraggableId(job.id)) ? vm.draggingSelectedCount : 0}
								vm={vm}
							/>
						))}
						{provided.placeholder}
					</Box>
				</Paper>
			)}
		</Droppable>
	);
}

// endregion

// region Main Component

export const UnmatchedDeals: React.FC = () => {
	const vm = useUnmatchedDealsViewModel();

	if (!vm.isDeveloper) {
		return (
			<Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: "md", margin: "auto" }}>
				<Alert severity="error">You do not have permission to access this page.</Alert>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				p: { xs: 2, sm: 4 },
				height: "calc(100vh - 64px)",
				boxSizing: "border-box",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Toolbar */}
			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
				<Typography variant="h5">Unmatched Deals</Typography>
				<Stack direction="row" spacing={1}>
					<Button
						variant="outlined"
						onClick={() => {
							const ids =
								vm.selectedJobs.size > 0 ? [...vm.selectedJobs].map(parseJobDraggableId) : vm.jobs.map((j) => j.id);
							navigator.clipboard.writeText(ids.join(","));
						}}
						disabled={vm.loading || vm.jobs.length === 0}
						startIcon={<ContentCopy />}
						size="small"
					>
						{vm.selectedJobs.size > 0 ? `Copy Selected IDs (${vm.selectedJobs.size})` : "Copy IDs"}
					</Button>
					<ButtonGroup size="small" disabled={vm.loading}>
						<Button
							variant={vm.dealScope === "company" ? "contained" : "outlined"}
							onClick={() => vm.setDealScope("company")}
						>
							Company
						</Button>
						<Button
							variant={vm.dealScope === "season" ? "contained" : "outlined"}
							onClick={() => vm.setDealScope("season")}
						>
							Season
						</Button>
						<Button variant={vm.dealScope === "all" ? "contained" : "outlined"} onClick={() => vm.setDealScope("all")}>
							All
						</Button>
					</ButtonGroup>
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={vm.includeInactive}
								onChange={(e) => vm.setIncludeInactive(e.target.checked)}
								disabled={vm.loading}
							/>
						}
						label="Include Inactive"
						slotProps={{ typography: { fontSize: "0.8rem" } }}
					/>
					<Button
						variant="contained"
						color="success"
						onClick={vm.applyAssignments}
						disabled={vm.pendingCount === 0 || vm.applying}
						startIcon={vm.applying ? <CircularProgress size={20} color="inherit" /> : <Check />}
						size="small"
					>
						{vm.applying ? "Applying..." : `Apply (${vm.pendingCount})`}
					</Button>
					<PortalTooltip title="Re-run Automatching">
						<Button
							variant="contained"
							onClick={vm.fixAll}
							disabled={vm.loading || vm.fixingAll || vm.jobs.length === 0}
							startIcon={vm.fixingAll ? <CircularProgress size={20} color="inherit" /> : undefined}
							size="small"
						>
							{vm.fixingAll
								? `Fixing${vm.selectedJobs.size > 0 ? " Selected" : " All"}... ${vm.countdown !== null ? `(${vm.countdown}s)` : ""}`
								: vm.selectedJobs.size > 0
									? `Fix Selected (${vm.selectedJobs.size})`
									: `Fix All (${vm.jobs.length})`}
						</Button>
					</PortalTooltip>
				</Stack>
			</Stack>

			{vm.error && (
				<Alert severity="error" sx={{ mb: 1 }}>
					{vm.error}
				</Alert>
			)}

			{vm.loading ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress />
				</Box>
			) : vm.jobs.length === 0 ? (
				<Alert severity="success">No unmatched deal jobs found.</Alert>
			) : (
				<DragDropContext onBeforeCapture={vm.handleBeforeCapture} onDragEnd={vm.handleDragEnd}>
					<Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0 }}>
						{/* Left panel: Unmatched jobs */}
						<Box sx={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
							<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
								<Typography variant="subtitle2">Unmatched ({vm.unassignedJobs.length})</Typography>
								{vm.selectedJobs.size > 0 && (
									<Chip
										label={`${vm.selectedJobs.size} selected`}
										size="small"
										onDelete={vm.clearSelection}
										sx={{ height: 22, fontSize: "0.7rem" }}
									/>
								)}
							</Stack>
							<Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
								<TextField
									size="small"
									placeholder="Search jobs..."
									value={vm.jobSearchText}
									onChange={(e) => vm.setJobSearchText(e.target.value)}
									slotProps={{
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<Search fontSize="small" />
												</InputAdornment>
											),
											sx: { fontSize: "0.8rem", height: 28 },
										},
									}}
									sx={{ flex: 1 }}
								/>
								<Select
									size="small"
									value={vm.jobSortOption}
									onChange={(e: SelectChangeEvent) => vm.setJobSortOption(e.target.value as any)}
									sx={{
										fontSize: "0.75rem",
										height: 28,
										minWidth: 0,
										"& .MuiSelect-select": { py: 0.25, pr: "24px !important", pl: 1 },
									}}
								>
									<MenuItem value="default" sx={{ fontSize: "0.8rem" }}>
										Default
									</MenuItem>
									<MenuItem value="grower" sx={{ fontSize: "0.8rem" }}>
										Grower
									</MenuItem>
									<MenuItem value="farm" sx={{ fontSize: "0.8rem" }}>
										Farm
									</MenuItem>
									<MenuItem value="field" sx={{ fontSize: "0.8rem" }}>
										Field
									</MenuItem>
								</Select>
							</Stack>
							<Droppable droppableId={UNASSIGNED_DROPPABLE_ID}>
								{(provided) => (
									<Box
										ref={provided.innerRef}
										{...provided.droppableProps}
										sx={{ flex: 1, overflowY: "auto", minHeight: 100 }}
									>
										{vm.unassignedJobs.map((job, index) => (
											<JobCard
												key={makeJobDraggableId(job.id)}
												job={job}
												index={index}
												isSelected={vm.selectedJobs.has(makeJobDraggableId(job.id))}
												onToggleSelect={vm.handleItemClick}
												multiDragCount={vm.selectedJobs.has(makeJobDraggableId(job.id)) ? vm.draggingSelectedCount : 0}
												vm={vm}
											/>
										))}
										{vm.unassignedJobs.length === 0 && (
											<Typography variant="caption" color="text.disabled" sx={{ fontStyle: "italic", p: 1 }}>
												{vm.jobSearchText.trim() ? "No jobs match search." : "All jobs assigned."}
											</Typography>
										)}
										{provided.placeholder}
									</Box>
								)}
							</Droppable>
						</Box>

						{/* Right panel: Deals grid */}
						<Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}>
							<DealsHeader vm={vm} />
							<Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
										gap: 1.5,
									}}
								>
									{vm.filteredSortedDeals.map((deal) => (
										<DealCard key={deal.id} deal={deal} vm={vm} />
									))}
								</Box>
								{vm.filteredSortedDeals.length === 0 && vm.deals.length > 0 && (
									<Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
										No deals match the current {vm.dealSearchText.trim() ? "search" : "filters"}.
									</Typography>
								)}
							</Box>
						</Box>
					</Box>
				</DragDropContext>
			)}
		</Box>
	);
};

// endregion
